/* ==========================================================================
   GW TRACKING — site-wide session + event tracking
   ==========================================================================
   Talks to the Django `tracking` app (tracking/urls.py). Responsible for:
     1. Creating/attaching a tracking session on every page (bootstrap_session)
     2. Capturing category / utm_source / utm_campaign / utm_medium from the
        link on first arrival and remembering it (first-touch) as well as the
        most recent one (last-touch). `source` is NOT read from the link —
        it is always the fixed value 'website' (see SOURCE below).
     3. Firing all the "core event" endpoints (page view, cart add/remove/
        update, checkout start, payment attempt, order complete, lab view)
     4. Exposing window.gwTracking so every other section/page can call
        gwTracking.track(...), gwTracking.attachIdentity(...), etc.
     5. Auto-tracking cart add/change/update by wrapping window.fetch, so
        every existing "/cart/add.js" & "/cart/change.js" call across the
        theme (main-product, main-cart, gw-collection-grid, gw-reorder,
        gw-health-benefits, featured-collection, gw-my-account …) gets
        tracked automatically — no need to touch each call site.
     6. appendSessionParams(url) — used to stamp session_id + the tracked
        link params onto iframe src URLs (quick-checkout, thank-you-embed)
        so the Django app knows the session even where a cross-domain cookie
        can't be relied on (see note at the bottom of this file).
     7. A persistent visitor_id, independent of session_id — session_id
        rotates (new TrackingSession per visit/expiry), visitor_id does
        NOT: it's generated once and kept in localStorage forever, so the
        backend can recognize "this is the same person" even across
        separate sessions.

   SOURCE / LINK PARAMS (current behaviour):
   The Django TrackingSession model stores exactly these link-derived fields:
       category      CharField(max_length=50, blank=True)
       utm_source    CharField(max_length=50, blank=True)
       utm_campaign  CharField(max_length=50, blank=True)
       utm_medium    CharField(max_length=50, blank=True)
   So this file reads ONLY those four from the URL (?category=…&utm_source=…
   &utm_campaign=…&utm_medium=…), trims them and caps them at 50 chars so a
   long value can never overflow the column, and sends '' (never null) when
   a param is absent, since the columns are blank=True but not null=True.
   `source` is always sent as 'website'. There is no more ?source= override,
   no gwTrackingContext.source override, and no referrer-hostname fallback
   into utm_source (that used to fill utm_source with e.g. "google.com" and
   utm_medium with "referral" on organic visits).
   The ad click IDs (fbclid, srsltid, ctwa_clid) are still forwarded because
   the backend needs them for Meta/Google attribution — they are identifiers,
   not source fields.

   IMPORTANT / READ THIS:
   Cross-domain cookies (SameSite=Lax) do NOT get sent on cross-site fetch()
   calls from the Shopify domain to dev1001.grannyways.in — only on real
   top-level navigations. That means the `gw_track` cookie the Django
   bootstrap_session() view sets can silently fail to "stick" across calls
   made from here. This file works around that by keeping the session_id in
   localStorage on the Shopify domain and sending it back explicitly, as
   `session_id`, in the body of every POST and the query string of every GET.
   For that to actually re-attach the same TrackingSession row (instead of a
   new one being created every call), tracking/views.py needs a small
   fallback added wherever it currently does `session = request.tracking_session`
   — e.g. in the middleware/helper that sets request.tracking_session, also
   check a posted "session_id" field / "X-GW-Session-Id" header before
   falling back to the cookie. Flagging this so events don't end up split
   across many phantom sessions.

   FIX (fbc_id / url mismatch at session creation):
   bootstrap() sends `landing_page` explicitly in the SAME bootstrap payload
   as `fbclid`, both read from the same `src` object at the same moment — so
   they can never diverge on this side. On the Django side,
   tracking/views.py's bootstrap_session() should read `url` straight from
   this posted field when saving the session's url/landing_page column,
   instead of (or in preference to) request.META.HTTP_REFERER.

   NOTE on visitor_id:
   tracking/views.py's bootstrap_session() should read `visitor_id` off
   this same payload and store it on the TrackingSession so repeat-visitor
   lookups don't depend on session_id, which is expected to expire/rotate.

   NOTE on repeat bootstraps:
   bootstrap() runs on every page load and re-sends the last-touch category/
   utm_* values each time, so bootstrap_session() should only WRITE those
   four columns when the session is being created (or when they are still
   empty) — not overwrite them on every call.

   DEBUG LOGGING:
   Off by default. Turn on in the browser console with
   localStorage.setItem('gw_debug', '1') and reload; turn off with
   localStorage.removeItem('gw_debug').
   ========================================================================== */
(function (window, document) {
  'use strict';

  // Set once, automatically, by layout/theme.liquid's "API environment
  // auto-detection" block, before this script loads.
  var API_ORIGIN = window.gwApiOrigin;

  // Fixed `source` sent to the backend on bootstrap and stamped on cart_add
  // events. Deliberately NOT derived from the URL, referrer or page context.
  var SOURCE = 'website';

  // Matches max_length=50 on the Django model fields.
  var FIELD_MAX = 50;

  // The only link params saved onto the session (Django model fields).
  var TRACKED_FIELDS = ['category', 'utm_source', 'utm_campaign', 'utm_medium'];

  // Ad click IDs — forwarded for attribution, not part of the source fields.
  var CLICK_ID_PARAMS = ['fbclid', 'srsltid', 'ctwa_clid'];

  var EP = {
    bootstrap:      API_ORIGIN + '/tracking/bootstrap/',
    sessionStatus:  API_ORIGIN + '/tracking/session-status/',
    ping:           API_ORIGIN + '/tracking/ping/',
    attachIdentity: API_ORIGIN + '/tracking/attach-identity/',
    addressSave:    API_ORIGIN + '/tracking/address/save/',
    pageLoad:       API_ORIGIN + '/tracking/page/load/',
    cartPrefill:    API_ORIGIN + '/tracking/cart/prefill/',
    cartAdd:        API_ORIGIN + '/tracking/cart/add/',
    cartRemove:     API_ORIGIN + '/tracking/cart/remove/',
    cartUpdate:     API_ORIGIN + '/tracking/cart/update/',
    checkoutStart:  API_ORIGIN + '/tracking/checkout/start/',
    paymentAttempt: API_ORIGIN + '/tracking/payment/attempt/',
    orderComplete:  API_ORIGIN + '/tracking/order/complete/',
    amazonClick:    API_ORIGIN + '/tracking/amazon/click/',
    labView:        API_ORIGIN + '/tracking/lab/view/',
    storeEvent:     API_ORIGIN + '/tracking/store/event/'
  };

  var LS = {
    SESSION:   'gw_session_id',
    // _v2: the old gw_src_first / gw_src_last could hold referrer hostnames
    // saved as utm_source (and 'referral' as utm_medium) by the previous
    // version. New keys mean returning visitors start clean instead of
    // continuing to send that stale data.
    SRC_FIRST: 'gw_src_first_v2',
    SRC_LAST:  'gw_src_last_v2',
    FBCLID:    'gw_fbclid',
    SRSLTID:   'gw_srsltid',
    CTWA_CLID: 'gw_ctwa_clid',
    VISITOR:   'gw_visitor_id',
    DEBUG:     'gw_debug'
  };

  // -------------------------------------------------------------------------
  // localStorage helpers (all wrapped — private/incognito or storage-disabled
  // browsers should never break tracking, just silently no-op)
  // -------------------------------------------------------------------------
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) {}
  }
  function lsGetJSON(key) {
    var raw = lsGet(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  function lsSetJSON(key, obj) {
    try { lsSet(key, JSON.stringify(obj)); } catch (e) {}
  }
  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp(
        '(?:^|;\\s*)' +
        name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
        '=([^;]*)'
      )
    );

    return match ? decodeURIComponent(match[1]) : null;
  }
  function getSessionId() { return lsGet(LS.SESSION); }
  function setSessionId(id) { if (id) lsSet(LS.SESSION, id); }

  // Console logging only when localStorage.gw_debug === '1'.
  var DEBUG = lsGet(LS.DEBUG) === '1';
  function debug() {
    if (DEBUG && window.console && console.log) {
      console.log.apply(console, arguments);
    }
  }

  // Trim + cap a link-derived value to the model's max_length. Always returns
  // a string ('' when empty) because the Django columns are blank=True, not
  // null=True — a null here could raise an IntegrityError on the backend.
  function clean(value) {
    if (value === null || value === undefined) return '';
    var v = String(value).trim();
    return v.length > FIELD_MAX ? v.slice(0, FIELD_MAX) : v;
  }

  // -------------------------------------------------------------------------
  // Visitor identity — persistent, independent of session_id.
  //
  // session_id belongs to one TrackingSession (backend rotates/expires it).
  // visitor_id is generated exactly once per browser/device and then just
  // read back forever from localStorage, so the backend can recognize
  // "same person" across many separate sessions.
  // -------------------------------------------------------------------------
  function generateVisitorId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // Fallback for older browsers without crypto.randomUUID.
    return 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function getVisitorId() {
    var id = lsGet(LS.VISITOR);
    if (!id) {
      id = generateVisitorId();
      lsSet(LS.VISITOR, id);
    }
    return id;
  }

  // -------------------------------------------------------------------------
  // Link param capture — category / utm_source / utm_campaign / utm_medium
  // (+ ad click IDs). Nothing else is read from the URL or the referrer.
  // -------------------------------------------------------------------------
  function captureSource() {
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var any = false;

    TRACKED_FIELDS.forEach(function (k) {
      var v = clean(params.get(k));
      if (v) { found[k] = v; any = true; }
    });

    var fbclid   = params.get('fbclid');
    var srsltid  = params.get('srsltid');   // Google Shopping/Merchant Center click ID
    var ctwaClid = params.get('ctwa_clid'); // Meta Click-to-WhatsApp Ads click ID
    if (fbclid)   { found.fbclid    = fbclid;   any = true; lsSet(LS.FBCLID, fbclid); }
    if (srsltid)  { found.srsltid   = srsltid;  any = true; lsSet(LS.SRSLTID, srsltid); }
    if (ctwaClid) { found.ctwa_clid = ctwaClid; any = true; lsSet(LS.CTWA_CLID, ctwaClid); }

    // landing_page is ALWAYS set (not gated behind `any`) — this is the one
    // field bootstrap() sends alongside fbclid, so both are guaranteed to be
    // read from window.location at the exact same moment, on every page,
    // regardless of whether any tracking params were present.
    found.landing_page = window.location.pathname + window.location.search;
    found.captured_at  = new Date().toISOString();

    if (any) {
      lsSetJSON(LS.SRC_LAST, found);
      if (!lsGetJSON(LS.SRC_FIRST)) lsSetJSON(LS.SRC_FIRST, found);
    }

    // If nothing new was captured on THIS page view, fall back to the
    // last-touch record already in localStorage — but landing_page/
    // captured_at always reflect THIS page view, never a stale one.
    var last = getLastTouch();
    return Object.assign({}, last, { landing_page: found.landing_page, captured_at: found.captured_at });
  }

  function getLastTouch() { return lsGetJSON(LS.SRC_LAST) || {}; }
  function getFirstTouch() { return lsGetJSON(LS.SRC_FIRST) || {}; }

  // -------------------------------------------------------------------------
  // Networking
  // -------------------------------------------------------------------------
  function postJSON(url, body) {
    var payload = Object.assign({ session_id: getSessionId() }, body || {});
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .catch(function (err) {
        console.error('[gw-tracking] request failed:', url, err);
        return { ok: false, status: 0, data: {} };
      });
  }

  function getJSON(url, extraParams) {
    var params = Object.assign({ session_id: getSessionId() || '' }, extraParams || {});
    var qs = Object.keys(params)
      .filter(function (k) { return params[k] !== null && params[k] !== undefined && params[k] !== ''; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]); })
      .join('&');
    return fetch(url + (qs ? '?' + qs : ''))
      .then(function (r) { return r.ok ? r.json() : {}; })
      .catch(function () { return {}; });
  }

  // -------------------------------------------------------------------------
  // Session bootstrap — runs once per page load
  // -------------------------------------------------------------------------
  var bootstrapPromise = null;

  function bootstrap() {
    if (bootstrapPromise) return bootstrapPromise;

    var src   = captureSource();
    var ctx   = window.gwTrackingContext || {};
    var phone = (window.gwAuth && window.gwAuth.getPhone && window.gwAuth.getPhone()) || null;

    // `source` is fixed. category / utm_source / utm_campaign / utm_medium
    // come from the link (this page's URL, or the last-touch record saved
    // earlier) and are always strings — '' when absent — to match the
    // blank=True / non-null Django columns.
    var bootstrapPayload = {
      phone:        phone,
      customer_id:  ctx.customerId || null,
      source:       SOURCE,
      category:     clean(src.category),
      utm_source:   clean(src.utm_source),
      utm_campaign: clean(src.utm_campaign),
      utm_medium:   clean(src.utm_medium),
      variant_id:   ctx.variantId || null,
      fbclid:       src.fbclid || lsGet(LS.FBCLID) || null,
      srsltid:      src.srsltid || lsGet(LS.SRSLTID) || null,
      ctwa_clid:    src.ctwa_clid || lsGet(LS.CTWA_CLID) || null,
      // landing_page (and full_url) always come from the exact same `src`
      // read as fbclid above, so the backend can save url/fbc_id from one
      // consistent source instead of pairing fbclid (from the query string)
      // with url (from a separate Referer header read).
      landing_page: src.landing_page,
      full_url:     window.location.href,
      // Persistent visitor identity — separate from session_id, which
      // postJSON() already stamps onto every request.
      visitor_id:   getVisitorId()
    };

    debug('[gw-tracking] bootstrap() SENDING:', Object.assign(
      { session_id: getSessionId() }, bootstrapPayload
    ));

    bootstrapPromise = postJSON(EP.bootstrap, bootstrapPayload).then(function (res) {
      debug('[gw-tracking] bootstrap() RESPONSE:', res.data);

      if (res.data && res.data.session_id) setSessionId(res.data.session_id);
      return res;
    });

    return bootstrapPromise;
  }

  // -------------------------------------------------------------------------
  // Public: track() — generic dispatcher for the "core event" endpoints
  // -------------------------------------------------------------------------
  var CORE_EVENT_ENDPOINTS = {
    page_view:       EP.pageLoad,
    cart_prefill:    EP.cartPrefill,
    cart_add:        EP.cartAdd,
    cart_remove:     EP.cartRemove,
    cart_update:     EP.cartUpdate,
    checkout_start:  EP.checkoutStart,
    payment_attempt: EP.paymentAttempt,
    order_complete:  EP.orderComplete,
    amazon_click:    EP.amazonClick,
    lab_test:        EP.labView
  };

  function track(eventType, data) {
    var url = CORE_EVENT_ENDPOINTS[eventType];

    if (!url) {
      console.error('[gw-tracking] unknown event type:', eventType);
      return Promise.resolve({ ok: false });
    }

    data = Object.assign({}, data || {});

    // -------------------------------------------------------------
    // Meta browser identifiers
    // These cookies exist on the Shopify domain.
    // They must be explicitly forwarded to Django because this is
    // a cross-domain request.
    // -------------------------------------------------------------

    var fbp = getCookie('_fbp');
    var fbc = getCookie('_fbc');

    // Build the Meta object expected by Django.
    data.meta = Object.assign({}, data.meta || {}, {
      fbp: data.meta && data.meta.fbp
        ? data.meta.fbp
        : fbp || null,

      fbc: data.meta && data.meta.fbc
        ? data.meta.fbc
        : fbc || null,

      event_source_url: (
        data.meta && data.meta.event_source_url
      ) || window.location.href,

      client_user_agent: (
        data.meta && data.meta.client_user_agent
      ) || navigator.userAgent,

      event_id: (
        data.meta && data.meta.event_id
      ) || data.event_id || null,

      external_id: (
        data.meta && data.meta.external_id
      ) || data.external_id || data.customer_id || null
    });

    debug('[gw-tracking] Meta data for', eventType, {
      fbp: !!data.meta.fbp,
      fbc: !!data.meta.fbc,
      event_id: data.meta.event_id,
      external_id: !!data.meta.external_id
    });

    return bootstrap().then(function () {
      return postJSON(url, data);
    });
  }

  // Universal store-side event endpoint (store_home_view, wallet_view, etc.
  // — mainly for the Django-rendered iframe pages themselves, exposed here
  // too in case a theme section ever needs to log one directly).
  function storeEvent(eventType, data) {
    return bootstrap().then(function () {
      return postJSON(EP.storeEvent, { event_type: eventType, data: data || {} });
    });
  }

  function attachIdentity(payload) {
    return bootstrap().then(function () { return postJSON(EP.attachIdentity, payload || {}); });
  }

  function saveAddress(payload) {
    return bootstrap().then(function () { return postJSON(EP.addressSave, payload || {}); });
  }

  function sessionStatus() {
    return getJSON(EP.sessionStatus);
  }

  // -------------------------------------------------------------------------
  // appendSessionParams(url) — stamp session_id + the tracked link params
  // (category / utm_source / utm_campaign / utm_medium / click IDs) onto a
  // URL before handing it to an <iframe src="...">, so the Django-rendered
  // page inside the iframe (quick-checkout, thank-you) can pick up the same
  // session/source even where the cross-domain cookie doesn't carry over.
  // -------------------------------------------------------------------------
  function appendSessionParams(url) {
    try {
      var u = new URL(url, window.location.href);
      var sid = getSessionId();
      var src = getLastTouch();
      if (sid) u.searchParams.set('session_id', sid);
      TRACKED_FIELDS.concat(CLICK_ID_PARAMS).forEach(function (k) {
        if (src[k]) u.searchParams.set(k, src[k]);
      });
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  // -------------------------------------------------------------------------
  // Auto-tracking: wrap fetch() to catch every /cart/add.js & /cart/change.js
  // call anywhere in the theme, without touching each call site.
  // -------------------------------------------------------------------------
  function installCartAutoTracking() {
    var nativeFetch = window.fetch;
    if (!nativeFetch || nativeFetch.__gwWrapped) return;

    function parseBody(init) {
      try {
        if (init && typeof init.body === 'string') return JSON.parse(init.body);
      } catch (e) {}
      return {};
    }

    // Most recent FULL cart snapshot ({items: [...]}) we've actually seen
    // from Shopify — kept fresh below on every /cart.js, /cart/change.js,
    // and /cart/update.js response. Needed because removing a line item
    // (qty -> 0) makes it vanish from the cart Shopify returns AFTER the
    // change — the removed line's title/price can only be read from the
    // cart as it looked *before* that request, which is this snapshot.
    var lastCartSnapshot = null;
    function updateSnapshot(cart) {
      if (cart && cart.items) lastCartSnapshot = cart;
    }

    // Shopify's /cart/change.js accepts either a numeric line INDEX or the
    // line item's composite "key" (variantId:propertiesHash) as `id`. For
    // tracking we only ever want the bare variant id — a raw key was
    // getting saved verbatim as e.g.
    // "48469075951773:7c1877533e844b9146485375011203f5".
    function bareVariantId(rawId) {
      if (typeof rawId === 'string' && rawId.indexOf(':') > -1) return rawId.split(':')[0];
      return rawId;
    }

    function findLine(cart, variantId, rawId) {
      if (!cart || !cart.items) return null;
      return cart.items.filter(function (i) {
        return String(i.variant_id) === String(variantId) ||
               String(i.id) === String(variantId) ||
               String(i.key) === String(rawId);
      })[0] || null;
    }

    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = nativeFetch.apply(window, arguments);

      if (/\/cart\/add\.js(\?|$)/.test(url)) {
        // Shopify's /cart/add.js returns two different response shapes
        // depending on the REQUEST body used: a flat single line-item
        // object when the request body was {id, quantity} (most
        // "Add to cart" buttons in this theme), or {items: [...]} when
        // the request used the bulk multi-item form
        // {items: [{id, quantity}, ...]} — which is what the cart
        // page's own upsell "Add" button sends. Reading this as if it
        // were always the flat shape (line.variant_id, line.price, etc.)
        // silently returned undefined/null for every field on a bulk
        // response, which is why cart_add events from that one button
        // were landing with price:null and no variant_id at all.
        // Normalizing to an array here means either shape — and any
        // future bulk-add call site — tracks correctly, without needing
        // every call site to agree on one request body format.

      promise.then(function (res) {
        if (!res.ok) return;

        res.clone().json().then(function (body) {
          if (!body || body.status) return;

          var lines = Array.isArray(body.items) ? body.items : [body];

          // Google Tag Manager: Add to Cart event
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'add_to_cart_event',
            ecommerce: {
              items: lines
                .filter(function (line) {
                  return !!line;
                })
                .map(function (line) {
                  return {
                    item_id: String(line.variant_id || line.id || ''),
                    item_name: line.product_title || line.title || '',
                    quantity: line.quantity || 1
                  };
                })
            }
          });

          // Existing Django tracking — keep this unchanged
          lines.forEach(function (line) {
            if (!line) return;

            var qty = line.quantity || 1;
            var price = typeof line.price === 'number'
              ? line.price / 100
              : null;

            track('cart_add', {
              variant_id: line.variant_id,
              product_id: line.product_id,
              title: line.title || line.product_title,
              qty: qty,
              price: price,
              line_total: typeof price === 'number'
                ? Math.round(price * qty * 100) / 100
                : null,
              url: window.location.pathname,
              source: SOURCE
            });
          });
        }).catch(function () {});
      }).catch(function () {});

      } else if (/\/cart\/change\.js(\?|$)/.test(url)) {
        var changeBody = parseBody(init);
        // Snapshot from BEFORE this change lands — the only place a
        // removed line's title/price (and its previous quantity, for
        // cart_update's old_qty) can still be found.
        var priorCart = lastCartSnapshot;
        promise.then(function (res) {
          if (!res.ok) return;
          res.clone().json().then(function (cart) {
            var newQty  = changeBody.quantity;
            var rawId   = changeBody.id || changeBody.line;
            var variantId = bareVariantId(rawId);
            var priorLine = findLine(priorCart, variantId, rawId);

            if (newQty === 0) {
              // Removed — it's already gone from `cart` (the post-change
              // response), so look it up in the pre-change snapshot instead.
              var removedQty   = priorLine ? priorLine.quantity : 0;
              var removedPrice = priorLine && typeof priorLine.price === 'number' ? priorLine.price / 100 : null;
              track('cart_remove', {
                variant_id: variantId,
                product_id: priorLine ? priorLine.product_id : undefined,
                title:      priorLine ? priorLine.title : undefined,
                qty:        removedQty,
                price:      removedPrice,
                line_total: typeof removedPrice === 'number' ? Math.round(removedPrice * removedQty * 100) / 100 : null
              });
            } else {
              var line  = findLine(cart, variantId, rawId);
              var price = line && typeof line.price === 'number' ? line.price / 100 : null;
              track('cart_update', {
                variant_id: variantId,
                product_id: line ? line.product_id : undefined,
                title:      line ? line.title : undefined,
                old_qty:    priorLine ? priorLine.quantity : undefined,
                new_qty:    newQty,
                price:      price,
                line_total: typeof price === 'number' ? Math.round(price * newQty * 100) / 100 : null
              });
            }

            updateSnapshot(cart);
          }).catch(function () {});
        }).catch(function () {});
      } else if (/\/cart\/update\.js(\?|$)/.test(url)) {
        promise.then(function (res) {
          if (!res.ok) return;
          res.clone().json().then(function (cart) {
            // Bulk update (cart page's "update all quantities" form) can
            // touch several lines at once — no single variant_id applies,
            // so this reports the whole resulting cart as `items`
            // (same per-line shape used everywhere else: qty, price,
            // title, line_total, product_id, variant_id) alongside the
            // existing item_count/total summary fields.
            var items = (cart.items || []).map(function (line) {
              var price = typeof line.price === 'number' ? line.price / 100 : null;
              return {
                variant_id: line.variant_id,
                product_id: line.product_id,
                title:      line.title,
                qty:        line.quantity,
                price:      price,
                line_total: typeof price === 'number' ? Math.round(price * line.quantity * 100) / 100 : null
              };
            });
            track('cart_update', {
              variant_id: 'bulk',
              items:      items,
              item_count: cart.item_count,
              total:      typeof cart.total_price === 'number' ? cart.total_price / 100 : null
            });
            updateSnapshot(cart);
          }).catch(function () {});
        }).catch(function () {});
      } else if (/\/cart\.js(\?|$)/.test(url) && (!init || !init.method || init.method.toUpperCase() === 'GET')) {
        // No tracking fired here — just keeps the snapshot fresh for the
        // next removal, since plenty of pages read /cart.js on load.
        promise.then(function (res) {
          if (!res.ok) return;
          res.clone().json().then(updateSnapshot).catch(function () {});
        }).catch(function () {});
      }

      return promise;
    };

    wrapped.__gwWrapped = true;
    window.fetch = wrapped;
  }

  // -------------------------------------------------------------------------
  // installTrackingParamCarry() — keeps the tracked link params (category,
  // utm_source, utm_campaign, utm_medium, fbclid, srsltid, ctwa_clid)
  // VISIBLE in the address bar as someone clicks from page to page across
  // the store, not just remembered internally for tracking. A normal
  // <a href="..."> click does NOT carry query params forward on its own —
  // that's just how browser navigation works, Shopify doesn't add this
  // either — so without this, ?utm_source=... on the homepage disappears
  // the moment someone clicks any link.
  //
  // How: rewrites every internal <a href> on the page UP FRONT, on load —
  // not just at the moment of clicking. A click-only version was tried
  // first and turned out unreliable: depending on how a given card/nav
  // item is built, the element actually receiving the click isn't always
  // a plain <a> at the moment it's clicked, so a click listener alone can
  // miss it. Rewriting hrefs immediately means you can inspect any link
  // in DevTools right after page load and see the param already baked
  // in — no guessing whether a click handler fired in time. A
  // MutationObserver keeps catching links added later (collection grids,
  // search results, infinite scroll), and the same rewrite also still
  // runs at click-time as a last-resort safety net.
  //
  // Deliberately left alone:
  //   - External links, mailto:, tel:, javascript:, and #anchors
  //   - A link that already explicitly sets one of these params itself
  //     (e.g. a WhatsApp template link with its own ?utm_source=whatsapp) —
  //     that explicit choice always wins over carrying the old one forward
  //   - Programmatic `window.location.href = ...` redirects elsewhere in
  //     the theme (login redirects, the header search box, "proceed to
  //     checkout") — those aren't <a> elements at all, so no href-rewrite
  //     approach can intercept them; ask if you need specific ones of
  //     those updated too.
  // -------------------------------------------------------------------------
  var CARRY_KEYS = TRACKED_FIELDS.concat(CLICK_ID_PARAMS);

  function trackingParamsToCarry() {
    var currentParams = new URLSearchParams(window.location.search);
    var last = getLastTouch();
    var out = {};
    CARRY_KEYS.forEach(function (k) {
      var v = currentParams.get(k) || last[k];
      if (v) out[k] = v;
    });
    return out;
  }

  function rewriteLinkHref(a, toCarry) {
    if (!a || a.__gwCarried) return; // already processed this element
    if (a.target && a.target !== '_self') return; // leave target="_blank" etc. alone
    if (a.hasAttribute('download')) return;

    var href = a.getAttribute('href');
    if (!href || /^(mailto:|tel:|javascript:|#)/i.test(href)) return;

    var url;
    try { url = new URL(href, window.location.href); } catch (err) { return; }
    if (url.hostname !== window.location.hostname) return; // external — don't touch

    var changed = false;
    Object.keys(toCarry).forEach(function (k) {
      if (url.searchParams.has(k)) return; // link already sets its own — don't override
      url.searchParams.set(k, toCarry[k]);
      changed = true;
    });

    if (changed) a.setAttribute('href', url.pathname + url.search + url.hash);
    a.__gwCarried = true; // mark done either way — a no-op link doesn't need rechecking
  }

  function rewriteAllLinks() {
    var toCarry = trackingParamsToCarry();
    if (!Object.keys(toCarry).length) return; // nothing to carry — skip the DOM scan entirely
    document.querySelectorAll('a[href]').forEach(function (a) {
      rewriteLinkHref(a, toCarry);
    });
  }

  function installTrackingParamCarry() {
    // 1. Rewrite everything already on the page.
    rewriteAllLinks();

    // 2. Catch links added later by other scripts (collection grids
    //    re-rendering, search-as-you-type results, infinite scroll).
    if (window.MutationObserver) {
      var observer = new MutationObserver(function (mutations) {
        var toCarry = trackingParamsToCarry();
        if (!Object.keys(toCarry).length) return;
        mutations.forEach(function (m) {
          m.addedNodes && m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return; // only element nodes
            if (node.matches && node.matches('a[href]')) rewriteLinkHref(node, toCarry);
            if (node.querySelectorAll) {
              node.querySelectorAll('a[href]').forEach(function (a) { rewriteLinkHref(a, toCarry); });
            }
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // 3. Safety net: re-check the specific link right before it navigates,
    //    in case something rewrote its href after step 1/2 ran (e.g. a
    //    theme script setting href based on a select/variant change).
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      a.__gwCarried = false; // force a recheck at the moment of the actual click
      rewriteLinkHref(a, trackingParamsToCarry());
    }, true); // capture phase — rewrite before any other click handler reads/uses href
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------
  window.gwTracking = {
    bootstrap:            bootstrap,
    track:                track,
    storeEvent:           storeEvent,
    attachIdentity:       attachIdentity,
    saveAddress:          saveAddress,
    sessionStatus:        sessionStatus,
    getSessionId:         getSessionId,
    getFirstTouch:        getFirstTouch,
    getLastTouch:         getLastTouch,
    appendSessionParams:  appendSessionParams,
    getVisitorId:         getVisitorId
  };

  // -------------------------------------------------------------------------
  // Init — bootstrap the session then fire a page_view for every page.
  // -------------------------------------------------------------------------
  installCartAutoTracking(); // only wraps window.fetch — safe to run before <body> exists

  function resolvePageTitle() {
    if (window.gwPageTitle) return window.gwPageTitle;
    return document.title.split(/[–|]/)[0].trim();
  }

  function init() {

    installTrackingParamCarry();

    bootstrap().then(function () {

      var skipPageView = false;
      try {
        if (sessionStorage.getItem('gw_skip_next_pageview') === '1') {
          skipPageView = true;
          sessionStorage.removeItem('gw_skip_next_pageview');
        }
      } catch (e) {}

      if (skipPageView) return;

      track('page_view', {
        url:        window.location.pathname + window.location.search,
        referrer:   document.referrer || null,
        title:      resolvePageTitle(),
        template:   (window.gwTrackingContext && window.gwTrackingContext.template) || null
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);


(function () {
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function updateBadges(count) {
    document.querySelectorAll('.cart-btn__count,.gw-mob-badge').forEach(function (badge) {
      badge.textContent = count;
      badge.dataset.count = count;
    });
  }

  /* productCache dedupes /products/{handle}.js fetches across lines
     that share the same product (different variant, same handle). */
  var productCache = {};
  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function stripUnavailable(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return Promise.resolve({ cart: cart, removed: [] });
    }
    var handles = [];
    cart.items.forEach(function (item) {
      if (handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });

    return Promise.all(handles.map(fetchProduct)).then(function (products) {
      var byHandle = {};
      handles.forEach(function (h, i) { byHandle[h] = products[i]; });

      var unavailableKeys = [];
      var removedTitles = [];
      cart.items.forEach(function (item) {
        var product = byHandle[item.handle];
        if (!product || !product.variants) return; // fetch failed — fail open, leave the line alone
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === item.variant_id) { variant = product.variants[i]; break; }
        }
        if (variant && variant.available === false) {
          unavailableKeys.push(item.key);
          removedTitles.push(item.title);
        }
      });

      if (unavailableKeys.length === 0) {
        return { cart: cart, removed: [] };
      }

      var removeCalls = unavailableKeys.map(function (key) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      });

      return Promise.all(removeCalls)
        .then(getCart)
        .then(function (freshCart) {
          updateBadges(freshCart.item_count);
          return { cart: freshCart, removed: removedTitles };
        });
    }).catch(function () { return { cart: cart, removed: [] }; });
  }

  window.gwCartGuard = {
    stripUnavailable: stripUnavailable
  };
})();